import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Check, X, Edit3, Save, AlertCircle, ChevronDown, 
  ChevronUp, Search, Filter, Loader, CheckCircle,
  XCircle, Clock, BarChart3
} from 'lucide-react'
import { chunkReviewAPI, extractionV2API } from '../../lib/api'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface ChunkProposal {
  id: string
  document_id: string
  chunk_number: number
  original_text?: string
  extracted_content: string
  extraction_metadata: {
    type: string
    confidence: number
    keywords: string[]
  }
  review_status: 'pending' | 'approved' | 'edited' | 'rejected'
  reviewed_by?: string
  reviewed_at?: string
  review_notes?: string
  final_content?: string
  created_at: string
}

interface ExtractionStats {
  total_proposals: number
  pending_review: number
  approved: number
  edited: number
  rejected: number
  approval_rate: number
}

interface ChunkReviewProps {
  documentId: string
  onClose?: () => void
}

export function ChunkReview({ documentId, onClose }: ChunkReviewProps) {
  const queryClient = useQueryClient()
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set())
  const [editingChunks, setEditingChunks] = useState<Map<string, string>>(new Map())
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractionStep, setExtractionStep] = useState<'none' | 'cleaning' | 'chunking' | 'complete'>('none')
  const [extractionStatus, setExtractionStatus] = useState<any>(null)

  // Fetch proposals
  const { data: proposals, isLoading: proposalsLoading, refetch, error: proposalsError } = useQuery(
    ['chunk-proposals', documentId, filterStatus],
    async () => {
      const result = await chunkReviewAPI.getProposals(documentId, filterStatus !== 'all' ? filterStatus : undefined) as ChunkProposal[]
      console.log('Fetched proposals:', result)
      
      // Check if all chunks are already approved (auto-approval)
      const allApproved = result.length > 0 && result.every(p => p.review_status === 'approved')
      if (allApproved) {
        console.log('All chunks are already auto-approved')
      }
      
      return result
    },
    {
      onError: (error) => {
        console.error('Error fetching proposals:', error)
      },
      staleTime: 5000, // Consider data fresh for 5 seconds
      cacheTime: 30000, // Keep in cache for 30 seconds
      refetchOnWindowFocus: false // Don't refetch on window focus
    }
  )

  // Fetch stats
  const { data: stats } = useQuery(
    ['extraction-stats', documentId],
    async () => {
      return await chunkReviewAPI.getStats(documentId) as ExtractionStats
    },
    { 
      refetchInterval: 30000, // Refresh every 30 seconds instead of 5
      staleTime: 10000 // Consider data fresh for 10 seconds
    }
  )

  // Review mutation
  const reviewMutation = useMutation(
    async ({ proposalId, review }: { proposalId: string; review: any }) => {
      return chunkReviewAPI.reviewProposal(proposalId, review)
    },
    {
      onSuccess: async () => {
        // Stagger the invalidations to avoid rate limiting
        await queryClient.invalidateQueries(['chunk-proposals', documentId])
        setTimeout(() => {
          queryClient.invalidateQueries(['extraction-stats', documentId])
        }, 1000)
      }
    }
  )

  // Bulk actions
  const bulkReviewMutation = useMutation(
    async (action: 'approve_all' | 'reject_all') => {
      return chunkReviewAPI.bulkReview(documentId, action)
    },
    {
      onSuccess: async () => {
        // Stagger refetch to avoid rate limiting
        setTimeout(() => {
          refetch()
        }, 500)
        setTimeout(() => {
          queryClient.invalidateQueries(['extraction-stats', documentId])
        }, 1500)
      }
    }
  )

  // Apply chunks mutation
  const applyChunksMutation = useMutation(
    async () => {
      return chunkReviewAPI.applyChunks(documentId)
    },
    {
      onSuccess: (data) => {
        // Refresh queries to update document status
        queryClient.invalidateQueries(['documents'])
        queryClient.invalidateQueries(['chunk-proposals', documentId])
        queryClient.invalidateQueries(['extraction-stats', documentId])
        
        alert(`Erfolgreich ${data.chunk_count} Chunks angewendet! Die Dokumente sind jetzt durchsuchbar.`)
        
        // Close after a short delay to show the success message
        setTimeout(() => {
          onClose?.()
        }, 1500)
      },
      onError: (error: any) => {
        console.error('Error applying chunks:', error)
        alert('Fehler beim Anwenden der Chunks: ' + (error.message || 'Unbekannter Fehler'))
      }
    }
  )

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedChunks)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedChunks(newExpanded)
  }

  const startEditing = (id: string, content: string) => {
    setEditingChunks(new Map(editingChunks.set(id, content)))
  }

  const cancelEditing = (id: string) => {
    const newEditing = new Map(editingChunks)
    newEditing.delete(id)
    setEditingChunks(newEditing)
  }

  const saveEdit = async (proposalId: string) => {
    const content = editingChunks.get(proposalId)
    if (!content) return

    await reviewMutation.mutateAsync({
      proposalId,
      review: {
        review_status: 'edited',
        final_content: content
      }
    })

    cancelEditing(proposalId)
  }

  const handleReview = async (proposalId: string, status: 'approved' | 'rejected') => {
    await reviewMutation.mutateAsync({
      proposalId,
      review: { review_status: status }
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'edited':
        return <Edit3 className="w-4 h-4 text-blue-500" />
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400'
      case 'edited':
        return 'bg-blue-500/20 text-blue-400'
      case 'rejected':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-yellow-500/20 text-yellow-400'
    }
  }

  const filteredProposals = proposals?.filter(p => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return p.extracted_content.toLowerCase().includes(search) ||
        p.extraction_metadata.keywords.some(k => k.toLowerCase().includes(search))
    }
    return true
  })

  // Check extraction status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await extractionV2API.getStatus(documentId)
        setExtractionStatus(status)
      } catch (error) {
        console.error('Error checking extraction status:', error)
      }
    }
    checkStatus()
  }, [documentId])

  // New V2 extraction methods
  const startCleaningV2 = async () => {
    setIsExtracting(true)
    setExtractionStep('cleaning')
    try {
      const result = await extractionV2API.cleanContent(documentId)
      console.log('Cleaning result:', result)
      setExtractionStatus({ ...extractionStatus, has_cleaned: true })
      setExtractionStep('none')
      alert(`Bereinigung abgeschlossen! ${result.reduction_percentage}% Reduktion`)
    } catch (error) {
      console.error('Error cleaning content:', error)
      alert('Fehler bei der Bereinigung')
    } finally {
      setIsExtracting(false)
    }
  }

  const startChunkingV2 = async () => {
    setIsExtracting(true)
    setExtractionStep('chunking')
    try {
      const result = await extractionV2API.extractChunks(documentId)
      console.log('Chunking result:', result)
      refetch()
      queryClient.invalidateQueries(['extraction-stats', documentId])
      queryClient.invalidateQueries(['documents']) // Refresh document list
      setExtractionStep('complete')
      
      // Check if chunks were auto-applied
      if (result.status === 'completed') {
        alert(`${result.chunks_count} Chunks erstellt und automatisch angewendet! Die Dokumente sind jetzt durchsuchbar.`)
        // Close the review dialog since chunks are already applied
        setTimeout(() => onClose?.(), 2000)
      } else {
        alert(`${result.chunks_count} Chunks erstellt! Bitte genehmigen und anwenden.`)
      }
    } catch (error) {
      console.error('Error chunking:', error)
      alert('Fehler beim Chunking')
    } finally {
      setIsExtracting(false)
    }
  }

  const processCompleteV2 = async () => {
    setIsExtracting(true)
    setExtractionStep('cleaning')
    try {
      const result = await extractionV2API.processComplete(documentId)
      console.log('Complete processing result:', result)
      refetch()
      queryClient.invalidateQueries(['extraction-stats', documentId])
      queryClient.invalidateQueries(['documents']) // Refresh document list
      setExtractionStep('complete')
      
      // Check if chunks were auto-applied
      if (result.status === 'completed') {
        alert(`Verarbeitung abgeschlossen! ${result.chunks_count} Chunks erstellt und automatisch angewendet. Die Dokumente sind jetzt durchsuchbar.`)
        // Close the review dialog since chunks are already applied
        setTimeout(() => onClose?.(), 2000)
      } else {
        alert(`Verarbeitung abgeschlossen! ${result.chunks_count} Chunks erstellt. Bitte genehmigen und anwenden.`)
      }
    } catch (error) {
      console.error('Error in complete processing:', error)
      alert('Fehler bei der Verarbeitung')
    } finally {
      setIsExtracting(false)
    }
  }

  // Start extraction if no proposals exist yet (OLD METHOD)
  const startExtraction = async () => {
    setIsExtracting(true)
    try {
      const result = await chunkReviewAPI.startExtraction(documentId, 'smart_chunking')
      console.log('Extraction result:', result)
      
      // If already ready for review, just refetch
      if (result.status === 'ready_for_review') {
        refetch()
        queryClient.invalidateQueries(['extraction-stats', documentId])
        setIsExtracting(false)
      } else {
        // Wait a bit and refetch
        setTimeout(() => {
          refetch()
          queryClient.invalidateQueries(['extraction-stats', documentId])
          setIsExtracting(false)
        }, 2000)
      }
    } catch (error) {
      console.error('Error starting extraction:', error)
      // Try to refetch anyway in case chunks already exist
      refetch()
      setIsExtracting(false)
    }
  }

  // Check if we need to show start extraction
  const needsExtraction = !proposalsLoading && (!proposals || proposals.length === 0) && !isExtracting

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold mb-2">Chunk-Überprüfung</h2>
            <p className="text-sm text-gray-400">Dokument ID: {documentId}</p>
          </div>
          
          {stats && (
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{stats.total_proposals}</p>
                <p className="text-xs text-gray-400">Gesamt</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-400">{stats.pending_review}</p>
                <p className="text-xs text-gray-400">Ausstehend</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{stats.approved + stats.edited}</p>
                <p className="text-xs text-gray-400">Genehmigt</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-400">
                  {Math.round(stats.approval_rate * 100)}%
                </p>
                <p className="text-xs text-gray-400">Quote</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions Bar */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Chunks durchsuchen..."
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
            />
          </div>

          {/* Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
          >
            <option value="all">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="approved">Genehmigt</option>
            <option value="edited">Bearbeitet</option>
            <option value="rejected">Abgelehnt</option>
          </select>

          {/* Bulk Actions */}
          {stats && stats.pending_review > 0 && (
            <>
              <button
                onClick={() => bulkReviewMutation.mutate('approve_all')}
                disabled={bulkReviewMutation.isLoading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Alle genehmigen
              </button>
              <button
                onClick={() => bulkReviewMutation.mutate('reject_all')}
                disabled={bulkReviewMutation.isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Alle ablehnen
              </button>
            </>
          )}

          {/* Apply Button - immer anzeigen wenn genehmigte Chunks existieren */}
          {stats && stats.approved + stats.edited > 0 && (
            <button
              onClick={() => applyChunksMutation.mutate()}
              disabled={applyChunksMutation.isLoading}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2"
            >
              {applyChunksMutation.isLoading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Chunks anwenden
            </button>
          )}
          
          {/* Back Button */}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
          >
            Zurück
          </button>
        </div>
      </div>

      {/* Auto-Approval Notice */}
      {proposals && proposals.length > 0 && proposals.every(p => p.review_status === 'approved') && stats && stats.approved > 0 && (
        <div className="glass rounded-lg p-4 bg-yellow-500/10 border border-yellow-500/20">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <p className="text-yellow-400">
              Alle {proposals.length} Chunks wurden genehmigt. 
              <strong> Sie müssen noch auf "Chunks anwenden" klicken</strong>, um sie für die Suche zu aktivieren!
            </p>
          </div>
        </div>
      )}

      {/* Chunks List */}
      <div className="space-y-4">
        {proposalsLoading ? (
          <div className="text-center py-8">
            <Loader className="w-8 h-8 mx-auto animate-spin" />
            <p className="mt-2 text-gray-400">Lade Chunks...</p>
          </div>
        ) : needsExtraction ? (
          <div className="glass rounded-lg p-8">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h3 className="text-lg font-semibold mb-4 text-center">Keine Chunks vorhanden</h3>
            
            {/* Extraction Status */}
            {extractionStatus && (
              <div className="mb-6 p-4 bg-white/5 rounded-lg">
                <h4 className="font-medium mb-2">Extraktionsstatus:</h4>
                <ul className="space-y-1 text-sm">
                  <li>Original vorhanden: {extractionStatus.has_original ? '✅' : '❌'}</li>
                  <li>Bereinigt: {extractionStatus.has_cleaned ? '✅' : '❌'}</li>
                  <li>Chunks: {extractionStatus.chunks_count || 0}</li>
                </ul>
              </div>
            )}
            
            <div className="space-y-4">
              <h4 className="font-medium text-center">Wählen Sie eine Extraktionsmethode:</h4>
              
              {/* V2 Two-Step Process */}
              <div className="p-4 border border-white/20 rounded-lg">
                <h5 className="font-medium mb-2">🆕 Empfohlen: 2-Schritt-Verfahren</h5>
                <p className="text-sm text-gray-400 mb-3">
                  Bessere Qualität durch getrennte Bereinigung und Chunking
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={startCleaningV2}
                    disabled={isExtracting || extractionStatus?.has_cleaned}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm"
                  >
                    {extractionStep === 'cleaning' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin inline mr-1" />
                        Bereinige...
                      </>
                    ) : (
                      '1. Bereinigen'
                    )}
                  </button>
                  
                  <button
                    onClick={startChunkingV2}
                    disabled={isExtracting || !extractionStatus?.has_cleaned}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded-lg transition-colors text-sm"
                  >
                    {extractionStep === 'chunking' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin inline mr-1" />
                        Chunke...
                      </>
                    ) : (
                      '2. Chunks erstellen'
                    )}
                  </button>
                </div>
              </div>
              
              {/* V2 Complete Process */}
              <div className="p-4 border border-white/20 rounded-lg">
                <h5 className="font-medium mb-2">⚖️ Komplett-Verarbeitung</h5>
                <p className="text-sm text-gray-400 mb-3">
                  Beide Schritte automatisch nacheinander
                </p>
                <button
                  onClick={processCompleteV2}
                  disabled={isExtracting}
                  className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {isExtracting && extractionStep !== 'none' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin inline mr-2" />
                      <span>Verarbeite...</span>
                    </>
                  ) : (
                    'Komplett verarbeiten'
                  )}
                </button>
              </div>
              
              {/* Old Method (Fallback) */}
              <div className="p-4 border border-white/10 rounded-lg opacity-50">
                <h5 className="font-medium mb-2">🔄 Alte Methode</h5>
                <p className="text-sm text-gray-400 mb-3">
                  Original 1-Schritt-Verfahren (Fallback)
                </p>
                <button
                  onClick={startExtraction}
                  disabled={isExtracting}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-sm"
                >
                  Alte Extraktion starten
                </button>
              </div>
            </div>
          </div>
        ) : filteredProposals?.length === 0 ? (
          <div className="text-center py-8 glass rounded-xl">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-400">Keine Chunks gefunden</p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredProposals?.map((proposal) => {
              const isExpanded = expandedChunks.has(proposal.id)
              const isEditing = editingChunks.has(proposal.id)
              
              return (
                <motion.div
                  key={proposal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={`glass rounded-xl overflow-hidden ${
                    (proposal.extraction_metadata as any)?.is_meta_chunk 
                      ? 'border-2 border-primary-500/30 bg-primary-500/5' 
                      : ''
                  }`}
                >
                  {/* Chunk Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-medium text-gray-400">
                            {(proposal.extraction_metadata as any)?.is_meta_chunk ? (
                              <span className="text-primary-400 font-semibold">📋 META-ÜBERSICHT</span>
                            ) : (
                              `Chunk #${proposal.chunk_number + 1}`
                            )}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusColor(proposal.review_status)}`}>
                            {getStatusIcon(proposal.review_status)}
                            <span className="ml-1">
                              {proposal.review_status === 'pending' ? 'Ausstehend' :
                               proposal.review_status === 'approved' ? 'Genehmigt' :
                               proposal.review_status === 'edited' ? 'Bearbeitet' : 'Abgelehnt'}
                            </span>
                          </span>
                          <span className="text-xs text-gray-500">
                            Konfidenz: {Math.round(proposal.extraction_metadata.confidence * 100)}%
                          </span>
                          {(proposal.extraction_metadata as any)?.is_meta_chunk && (
                            <span className="text-xs text-primary-400 font-medium">
                              Strukturiert {filteredProposals.length - 1} Detail-Chunks
                            </span>
                          )}
                        </div>
                        
                        {/* Content Preview/Edit */}
                        {isEditing ? (
                          <textarea
                            value={editingChunks.get(proposal.id)}
                            onChange={(e) => setEditingChunks(new Map(editingChunks.set(proposal.id, e.target.value)))}
                            className="w-full p-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
                            rows={6}
                          />
                        ) : (
                          <p className="text-sm leading-relaxed">
                            {proposal.final_content || proposal.extracted_content}
                          </p>
                        )}
                        
                        {/* Keywords */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {proposal.extraction_metadata.keywords.map((keyword, i) => (
                            <span key={i} className="px-2 py-1 bg-primary-600/20 text-primary-400 rounded text-xs">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        {proposal.review_status === 'pending' && !isEditing && (
                          <>
                            <button
                              onClick={() => handleReview(proposal.id, 'approved')}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 transition-colors"
                              title="Genehmigen"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditing(proposal.id, proposal.extracted_content)}
                              className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded text-blue-400 transition-colors"
                              title="Bearbeiten"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReview(proposal.id, 'rejected')}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 transition-colors"
                              title="Ablehnen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {isEditing && (
                          <>
                            <button
                              onClick={() => saveEdit(proposal.id)}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 transition-colors"
                              title="Speichern"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => cancelEditing(proposal.id)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 transition-colors"
                              title="Abbrechen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        <button
                          onClick={() => toggleExpanded(proposal.id)}
                          className="p-2 hover:bg-white/10 rounded transition-colors"
                          title={isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-white/10"
                      >
                        <div className="p-4 space-y-4">
                          {proposal.original_text && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Original-Ausschnitt:</h4>
                              <p className="text-sm text-gray-400 bg-black/20 p-3 rounded">
                                {proposal.original_text}
                              </p>
                            </div>
                          )}
                          
                          {proposal.reviewed_at && (
                            <div className="text-xs text-gray-500">
                              Überprüft am {format(new Date(proposal.reviewed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                              {proposal.review_notes && (
                                <p className="mt-1">Notiz: {proposal.review_notes}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}